import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { dayInTz } from '../../lib/format';

/**
 * What the card says about a sent quote's follow-ups, on the shop's calendar. Without the
 * shop's zone (still loading, or it failed) it names no day rather than guess one.
 */
export function followUpLine(
  sentAt: Date | string | null,
  followedUpAt: Date | string | null,
  tz: string | null,
): string {
  if (!tz)
    return followedUpAt ? 'A follow-up has been logged.' : 'No follow-up logged yet.';
  const sent = sentAt ? `Sent ${dayInTz(sentAt, tz)}. ` : '';
  return followedUpAt
    ? `${sent}Last follow-up logged ${dayInTz(followedUpAt, tz)}.`
    : `${sent}No follow-up logged yet.`;
}

/**
 * "Log a follow-up" on a sent quote (ops insights Phase 7 spec §5): the office chased the
 * customer by phone, text or in person, and says so. The "Quote not followed up" alert counts
 * from it, as it does from a re-send or a delivered automatic follow-up.
 *
 * No hooks: the page owns the write and hands the card its state, so a test can render and
 * press it as the function it is. `aria-disabled`, not `disabled`, while a write on this quote
 * saves: the press is ignored, and focus stays on the button (LESSONS 2026-09-28).
 */
export function QuoteFollowUpCard({
  sentAt,
  followedUpAt,
  tz,
  pending,
  locked,
  error,
  onLog,
}: {
  sentAt: Date | string | null;
  followedUpAt: Date | string | null;
  /** The shop's zone, or null while it is not known. */
  tz: string | null;
  /** This write is saving. */
  pending: boolean;
  /** Another write on this quote is saving. */
  locked: boolean;
  /** The refusal, in words, beside the button it answers. */
  error: string | null;
  onLog: () => void;
}) {
  const busy = pending || locked;
  return (
    <SectionCard id="follow-up" title="Follow-up">
      <p className="mb-3 text-sm text-muted-foreground">
        {followUpLine(sentAt, followedUpAt, tz)}
      </p>
      {error ? (
        <div className="mb-3">
          <Notice tone="danger" alert>
            {error}
          </Notice>
        </div>
      ) : null}
      <Button
        variant="ghost"
        aria-disabled={busy}
        onClick={() => {
          if (busy) return;
          onLog();
        }}
      >
        {pending ? 'Logging…' : 'Log a follow-up'}
      </Button>
      <p className="mt-2 text-xs text-muted-foreground">
        Called, texted or saw them about it? Log it, so the quote stops waiting on a
        follow-up.
      </p>
    </SectionCard>
  );
}
